/**
 * Risk across every account at once.
 *
 * ## It renders nothing for a single-account trader
 *
 * A portfolio of one is not a portfolio, and a panel headed "across your
 * accounts" over a single row is a feature announcing itself rather than
 * telling anybody anything. The whole component returns null below two.
 *
 * ## What it says that the per-account panel cannot
 *
 * Three things, and each is a place where the per-account view is individually
 * correct and collectively misleading:
 *
 * 1. **Total exposure by MARKET**, with micros folded into their parent. Long
 *    2 ES at one firm and 10 MES at another is three ES of one bet.
 * 2. **Which account fails first**, and at what price. Exactly computable, no
 *    correlation assumption — it is one market moving one way.
 * 3. **Opposite sides across accounts**, which looks like a hedge and is the
 *    opposite of one under prop-firm rules.
 *
 * Everything is a count, a distance or an ordering. CLAUDE.md non-negotiable 4.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { fmtNum, fmtUsd } from '../lib/api.js';
import { api } from '../lib/api.js';
import type { BreachPointDto, PortfolioRiskResponse } from '../lib/api.js';

/** Group the ordering by market and direction — one column per scenario. */
function scenarios(order: readonly BreachPointDto[]): Array<{
  key: string; market: string; moves: 'up' | 'down'; points: readonly BreachPointDto[];
}> {
  const by = new Map<string, BreachPointDto[]>();
  for (const b of order) {
    const key = `${b.market}:${b.moves}`;
    const list = by.get(key);
    if (list) list.push(b); else by.set(key, [b]);
  }
  return [...by.entries()].map(([key, points]) => ({
    key, market: points[0]!.market, moves: points[0]!.moves, points,
  }));
}

export function PortfolioRiskPanel(): JSX.Element | null {
  const [risk, setRisk] = useState<PortfolioRiskResponse | null>(null);

  useEffect(() => {
    let live = true;
    const load = () => {
      void api.portfolioRisk()
        .then((r) => { if (live) setRisk(r); })
        .catch(() => { /* One panel failing is never worth blanking a page. */ });
    };
    load();
    // Positions move. Slower than a tick, because nothing here changes on one.
    const timer = setInterval(load, 20_000);
    return () => { live = false; clearInterval(timer); };
  }, []);

  // Below two accounts there is nothing to aggregate, and a heading over one
  // row is a feature announcing itself.
  if (!risk || risk.accountCount < 2) return null;

  const groups = scenarios(risk.order);

  return (
    <section className="portfolio" aria-label="Risk across your accounts">
      <h3 className="panel-h">ACROSS YOUR ACCOUNTS</h3>

      <div className="pf-head">
        <div>
          <div className="pf-equity mono">{fmtUsd(risk.totalEquity)}</div>
          <div className="pf-sub">
            {risk.accountCount} accounts · open{' '}
            <span className="mono">{fmtUsd(risk.totalUnrealized, { sign: true })}</span>
          </div>
        </div>
      </div>

      {/* --- exposure, by market rather than by contract ------------------- */}
      {risk.exposures.length > 0 && (
        <div className="pf-block">
          <h4 className="mono">TOTAL EXPOSURE</h4>
          {risk.exposures.map((e) => (
            <div key={e.market} className={`pf-market${e.offsetting ? ' offsetting' : ''}`}>
              <div className="pf-market-top">
                <span className="pf-sym">{e.market}</span>
                <span className="pf-net mono">
                  {Number(e.net) === 0 ? 'flat' : `${Number(e.net) > 0 ? 'long' : 'short'} ${fmtNum(Math.abs(Number(e.net)), 2)}`}
                </span>
                <span className="pf-gross mono">{fmtNum(e.gross, 2)} gross</span>
              </div>
              <ul className="pf-legs">
                {e.legs.map((l, i) => (
                  <li key={`${l.accountId}-${l.symbol}-${i}`}>
                    <span className="pf-leg-acct">{l.nickname}</span>
                    <span className={`pf-leg-dir dir-${l.direction}`}>
                      {l.direction === 'long' ? 'LONG' : 'SHORT'} {fmtNum(l.quantity, 0)} {l.symbol}
                    </span>
                    {/* Only when the contract is not the market — the whole
                        point of folding micros in. */}
                    {l.symbol !== e.market && (
                      <span className="pf-leg-eq mono">= {fmtNum(l.equivalent, 2)} {e.market}</span>
                    )}
                  </li>
                ))}
              </ul>
              {e.note && <p className="pf-note">{e.note}</p>}
            </div>
          ))}
        </div>
      )}

      {/* --- which account fails first ------------------------------------- */}
      {groups.length > 0 && (
        <div className="pf-block">
          <h4 className="mono">IF ONE MARKET MOVES</h4>
          {groups.map((g) => (
            <div key={g.key} className="pf-scenario">
              <div className="pf-scenario-head">
                {g.market} {g.moves === 'down' ? 'falls' : 'rises'}
              </div>
              <ol className="pf-order">
                {g.points.map((b, i) => (
                  <li key={`${b.accountId}-${b.ruleId}`}>
                    <span className="pf-pts mono">{fmtNum(b.points, 2)} pts</span>
                    {/* Named, not implied: "first" is the useful word, and it
                        sits next to the distance it qualifies rather than
                        being pushed to the end of a wrapping row. */}
                    {i === 0 && <span className="pf-first">first</span>}
                    <span className="pf-acct">{b.nickname}</span>
                    <span className="pf-rule">{b.label.toLowerCase()}</span>
                    <span className="pf-price mono">at {fmtNum(b.price, 2)}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}

      {/* --- what is missing from the figures ------------------------------ */}
      {risk.excludedForCurrency.length > 0 && (
        <p className="pf-caveat">
          {risk.excludedForCurrency.length} account
          {risk.excludedForCurrency.length === 1 ? '' : 's'} in another currency
          {risk.excludedForCurrency.length === 1 ? ' is' : ' are'} left out of the totals.
          AEGIS will not convert at a rate it would have to pick.
        </p>
      )}
      {risk.alreadyBreached.length > 0 && (
        <p className="pf-caveat">
          {risk.alreadyBreached.length} account
          {risk.alreadyBreached.length === 1 ? ' has' : 's have'} already breached, so
          {risk.alreadyBreached.length === 1 ? ' it is' : ' they are'} not in the ordering
          above — that is a settled outcome, not a forecast.
        </p>
      )}
      {risk.unavailable.length > 0 && (
        <p className="pf-caveat">
          {risk.unavailable.length} account
          {risk.unavailable.length === 1 ? '' : 's'} could not be read just now and
          {risk.unavailable.length === 1 ? ' is' : ' are'} missing from every figure here.
        </p>
      )}
      {risk.unpriced.length > 0 && (
        <p className="pf-caveat">
          <strong>{risk.unpriced.join(', ')}</strong> {risk.unpriced.length === 1 ? 'is' : 'are'}
          {' '}not in the instrument master and {risk.unpriced.length === 1 ? 'is' : 'are'} excluded.
        </p>
      )}
    </section>
  );
}
